/**
 * usePaletteDnD — wspólny hook drag&drop dla kreatorów layoutu (F12.N D2, 2026-05-15).
 *
 * Wcześniej paleta po lewej stronie kreatora była click-to-add (klik na pole/element
 * dodawał go do canvasu). W F12.N MVP testowym dorzucamy drag&drop — admin może
 * przeciągnąć klocek z palety i upuścić na konkretną sekcję canvasu (UX z SellCall).
 *
 * Architektura:
 *  - Jeden top-level `<DndContext>` na całej stronie kreatora (pakuje paletę + canvas).
 *  - Wszystkie `useSortable` items na canvasie (sekcje, elementy) dodatkowo niosą
 *    `data.target = 'section'` z `sectionId` rodzica — dzięki temu palette item upuszczony
 *    na element trafia do sekcji rodzica (a nie wymaga osobnego `useDroppable`).
 *  - Palette items używają `useDraggable` z `data.source = 'palette'` + `kind` (`field`
 *    lub `layout-element`) + `payload` (fieldId / type).
 *
 * Hook zwraca `sensors` + `onDragEnd` rozdzielający 3 ścieżki:
 *   1. drag z palety → `onAddFromPalette(kind, payload, sectionId)`
 *   2. drag sekcji (sort) → `onSectionReorder(activeId, overId)`
 *   3. drag elementu (sort wewnątrz sekcji) → `onElementReorder(sectionId, activeId, overId)`
 *
 * Bez DragOverlay (opcjonalny UX dla MVP) i bez drop zone w empty canvas — admin
 * klika „+ Dodaj sekcję" zanim pierwszy drag (zgodnie z decyzją produktową D2).
 */

import { useMemo } from 'react';
import {
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

export type PaletteDragKind = 'field' | 'layout-element';

export interface PaletteDragData {
  source: 'palette';
  kind: PaletteDragKind;
  /** Dla `field` to `fieldId` (UUID), dla `layout-element` to `type` (np. „card", „alert"). */
  payload: string;
}

export interface SectionDragData {
  source: 'section';
  /** Sekcja jest jednocześnie drop target — `addFromPalette` dropowane bezpośrednio na sekcję. */
  target: 'section';
  sectionId: string;
}

export interface ElementDragData {
  source: 'element';
  /** Element też jest drop target — palette item upuszczony na element trafia do sekcji rodzica. */
  target: 'section';
  sectionId: string;
}

/**
 * Pakiet B Sub-tura 2 — panel akordeonu jako source (sortowanie paneli w obrębie
 * jednego akordeonu). NIE jest też drop targetem dla palety bezpośrednio (drop
 * pola/elementu na CONTENT panelu trafia do `SectionDragData{target: 'section', sectionId: panelId}`
 * skonfigurowanego osobno w `AccordionPanelInCanvas` przez `useDroppable`).
 */
export interface AccordionPanelDragData {
  source: 'accordion-panel';
  accordionId: string;
  panelId: string;
}

/**
 * Etap 3 T2 — krok steppera/wizarda jako source (sortowanie kroków w obrębie
 * jednego steppera). Cross-stepper reorder nie obsługujemy w MVP.
 */
export interface StepperStepDragData {
  source: 'stepper-step';
  stepperId: string;
  stepId: string;
}

export type DragSourceData =
  | PaletteDragData
  | SectionDragData
  | ElementDragData
  | AccordionPanelDragData
  | StepperStepDragData;
export type DropTargetData = SectionDragData | ElementDragData;

function isDragSourceData(value: unknown): value is DragSourceData {
  if (!value || typeof value !== 'object') return false;
  const v = value as { source?: unknown };
  return (
    v.source === 'palette' ||
    v.source === 'section' ||
    v.source === 'element' ||
    v.source === 'accordion-panel' ||
    v.source === 'stepper-step'
  );
}

function isDropTargetData(value: unknown): value is DropTargetData {
  if (!value || typeof value !== 'object') return false;
  const v = value as { target?: unknown; sectionId?: unknown };
  return v.target === 'section' && typeof v.sectionId === 'string';
}

export interface UsePaletteDnDCallbacks {
  /** Wywoływane gdy admin upuścił klocek z palety na sekcję canvasu. */
  onAddFromPalette: (kind: PaletteDragKind, payload: string, sectionId: string) => void;
  /** Wywoływane gdy admin przeciągnął sekcję by zmienić kolejność. */
  onSectionReorder: (activeId: string, overId: string) => void;
  /**
   * Wywoływane gdy admin przeciągnął element (pole / non-field element).
   * `sourceContainerId` = id sekcji/panelu w którym element był; `targetContainerId` = id sekcji/panelu
   * dokąd jedzie. Gdy oba identyczne — same-container reorder (Sub-2). Gdy różne — cross-container
   * move (Sub-3). `overId` = id elementu pod kursorem w target container (referencja indexu) lub
   * id samego target container jeśli puszczono na pusty obszar sekcji.
   */
  onElementReorder: (
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) => void;
  /**
   * Wywoływane gdy admin przeciągnął panel akordeonu by zmienić kolejność paneli
   * w obrębie tego samego akordeonu (cross-accordion reorder nie obsługujemy w MVP).
   * Pakiet B Sub-2.
   */
  onAccordionPanelReorder: (accordionId: string, activeId: string, overId: string) => void;
  /**
   * Wywoływane gdy admin przeciągnął krok steppera/wizarda by zmienić kolejność
   * w obrębie jednego steppera. Cross-stepper reorder nie obsługujemy w MVP. Etap 3 T2.
   */
  onStepReorder: (stepperId: string, activeId: string, overId: string) => void;
}

export function usePaletteDnD(callbacks: UsePaletteDnDCallbacks) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // useMemo żeby callback referencja była stabilna — DndContext nie reseta sensors per render.
  const onDragEnd = useMemo(
    () => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const sourceData = isDragSourceData(active.data.current) ? active.data.current : null;
      const targetData = isDropTargetData(over.data.current) ? over.data.current : null;

      // 1. Drag z palety → dodaj klocek do sekcji.
      if (sourceData?.source === 'palette') {
        if (!targetData) return; // upuszczone poza canvasem
        callbacks.onAddFromPalette(sourceData.kind, sourceData.payload, targetData.sectionId);
        return;
      }

      // 2. Sort sekcji.
      if (sourceData?.source === 'section') {
        if (String(active.id) === String(over.id)) return;
        callbacks.onSectionReorder(String(active.id), String(over.id));
        return;
      }

      // 3. Sort elementów (same-container reorder LUB cross-container move). Sub-2 + Sub-3.
      //   - `sourceContainerId` = sourceData.sectionId (element's parent container).
      //   - `targetContainerId` = over.sectionId; over może być Section lub Element (oba mają target='section').
      if (sourceData?.source === 'element') {
        if (String(active.id) === String(over.id)) return;
        if (!targetData) return; // upuszczone poza canvasem
        callbacks.onElementReorder(
          sourceData.sectionId,
          String(active.id),
          targetData.sectionId,
          String(over.id),
        );
        return;
      }

      // 4. Sort paneli akordeonu w obrębie jednego akordeonu (pakiet B Sub-2).
      // Cross-accordion drop nie obsługujemy w MVP — over musi mieć ten sam accordionId.
      if (sourceData?.source === 'accordion-panel') {
        const overData = over.data.current;
        const sameAccordion =
          overData &&
          typeof overData === 'object' &&
          (overData as { source?: unknown }).source === 'accordion-panel' &&
          (overData as { accordionId?: unknown }).accordionId === sourceData.accordionId;
        if (!sameAccordion) return;
        if (String(active.id) === String(over.id)) return;
        callbacks.onAccordionPanelReorder(
          sourceData.accordionId,
          String(active.id),
          String(over.id),
        );
        return;
      }

      // 5. Sort kroków steppera/wizarda (Etap 3 T2). Cross-stepper drop nie obsługujemy
      // w MVP — over musi mieć ten sam stepperId.
      if (sourceData?.source === 'stepper-step') {
        const overData = over.data.current;
        const sameStepper =
          overData &&
          typeof overData === 'object' &&
          (overData as { source?: unknown }).source === 'stepper-step' &&
          (overData as { stepperId?: unknown }).stepperId === sourceData.stepperId;
        if (!sameStepper) return;
        if (String(active.id) === String(over.id)) return;
        callbacks.onStepReorder(sourceData.stepperId, String(active.id), String(over.id));
        return;
      }
    },
    [callbacks],
  );

  return { sensors, onDragEnd };
}
